import { useCallback, useMemo, useState, memo } from "react";
import { Plus, X, ChevronRight, Wrench } from "lucide-react";
import { cn } from "@/utilities/tailwind-utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useSkillsFromLookupContext,
  useSkillCategoriesFromLookupContext,
  useExperienceLevelsFromLookupContext,
} from "@/context/use-lookups";
import type {
  Skill,
  SkillCategory,
  SkillRecord,
} from "@/domain/professionals";
import { MAX_SKILLS } from "@/constants";
import { yearsOfExperienceFromLevelId } from "@/utilities/app-utils";

interface RegistrationSkillPickerProps {
  value: SkillRecord[];
  onChange: (skills: SkillRecord[]) => void;
}

// Registration skill picker component
export const RegistrationSkillPicker = memo(function RegistrationSkillPicker({
  value,
  onChange,
}: RegistrationSkillPickerProps) {
  const skillsLookup = useSkillsFromLookupContext();
  const skillCategories = useSkillCategoriesFromLookupContext();
  const experienceLevels = useExperienceLevelsFromLookupContext();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [selectedExperienceLevel, setSelectedExperienceLevel] = useState("");

  const addedSkillIds = useMemo(
    () => new Set(value.map((s) => s.skillId)),
    [value]
  );

  const skillTitleById = useMemo(
    () => new Map(skillsLookup.map((s) => [s.id, s.title])),
    [skillsLookup]
  );

  const groupedSkills = useMemo(() => {
    const groups = skillCategories
      .map((cat: SkillCategory) => ({
        id: cat.id,
        title: cat.title,
        items: skillsLookup.filter((s: Skill) => s.categoryId === cat.id),
      }))
      .filter((g) => g.items.length > 0);

    const groupedIds = new Set(groups.flatMap((g) => g.items.map((s) => s.id)));
    const uncategorized = skillsLookup.filter((s) => !groupedIds.has(s.id));
    if (uncategorized.length > 0) {
      groups.push({
        id: "uncategorized",
        title: "Other",
        items: uncategorized,
      });
    }
    return groups;
  }, [skillCategories, skillsLookup]);

  const atMax = value.length >= MAX_SKILLS;
  const lookupsReady = skillsLookup.length > 0;

  const handleOpenDialog = useCallback(() => {
    if (atMax || !lookupsReady) return;
    setSelectedSkill(null);
    setSelectedExperienceLevel("");
    setIsDialogOpen(true);
  }, [atMax, lookupsReady]);

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setSelectedSkill(null);
      setSelectedExperienceLevel("");
    }
  }, []);

  const handleSelectSkill = useCallback((skill: Skill) => {
    setSelectedSkill(skill);
    setSelectedExperienceLevel("");
  }, []);

  const handleAdd = useCallback(() => {
    if (!selectedSkill || addedSkillIds.has(selectedSkill.id) || atMax) return;

    const years = yearsOfExperienceFromLevelId(
      experienceLevels,
      selectedExperienceLevel
    );
    const next: SkillRecord[] = [
      ...value,
      {
        skillId: selectedSkill.id,
        ...(years !== undefined ? { yearsOfExperience: years } : {}),
      },
    ];
    onChange(next);
    setSelectedSkill(null);
    setSelectedExperienceLevel("");
    setIsDialogOpen(false);
  }, [
    selectedSkill,
    addedSkillIds,
    atMax,
    experienceLevels,
    selectedExperienceLevel,
    value,
    onChange,
  ]);

  const handleRemove = useCallback(
    (skillId: string) => {
      onChange(value.filter((s) => s.skillId !== skillId));
    },
    [onChange, value]
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor="skills">Select your skills</Label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleOpenDialog}
          disabled={atMax || !lookupsReady}
          className="gap-1.5"
        >
          <Plus className="size-3.5" />
          Add
        </Button>
      </div>

      {value.length === 0 ? (
        <button
          type="button"
          id="skills"
          onClick={handleOpenDialog}
          disabled={!lookupsReady}
          className={cn(
            "w-full flex flex-col items-center justify-center py-6 rounded-md",
            "border border-dashed border-input bg-background dark:bg-input/30",
            "hover:border-primary/40 hover:bg-muted/20 transition-colors",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
        >
          <p className="text-sm font-medium text-muted-foreground">
            Add your first skill
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Search skills and set years of experience
          </p>
        </button>
      ) : (
        <div className="flex flex-wrap gap-2 rounded-md border border-input bg-background dark:bg-input/30 p-2 min-h-9">
          {value.map((record) => (
            <div
              key={record.skillId}
              className={cn(
                "group flex items-center gap-2 rounded-lg px-3 py-2",
                "border border-border/50 dark:border-white/[0.06]",
                "bg-muted/20 dark:bg-white/[0.02]"
              )}
            >
              <span className="text-sm font-medium text-foreground">
                {skillTitleById.get(record.skillId) ?? "Skill"}
              </span>
              {record.yearsOfExperience != null && (
                <span className="text-xs px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium">
                  {record.yearsOfExperience}y exp
                </span>
              )}
              <button
                title="Remove skill"
                type="button"
                onClick={() => handleRemove(record.skillId)}
                className={cn(
                  "ml-1 rounded-full p-0.5",
                  "text-muted-foreground/40 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30",
                  "opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                )}
              >
                <X className="size-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        Select up to {MAX_SKILLS} skills that reflect your strengths, and set
        years of experience for each. This helps us match you with the right
        shifts.
      </p>

      <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5 pb-3">
            <DialogTitle className="text-base">Add Skill</DialogTitle>
            <DialogDescription className="text-xs">
              Search and select a skill, then optionally set years of experience
            </DialogDescription>
          </DialogHeader>

          {!selectedSkill ? (
            <Command className="border-t">
              <CommandInput placeholder="Search skills..." />
              <CommandList className="max-h-64">
                <CommandEmpty>No skills found.</CommandEmpty>
                {groupedSkills.map((group) => (
                  <CommandGroup key={group.id} heading={group.title}>
                    {group.items.map((skill) => {
                      const isAdded = addedSkillIds.has(skill.id);
                      return (
                        <CommandItem
                          key={skill.id}
                          value={`${skill.title} ${skill.id}`}
                          onSelect={() => !isAdded && handleSelectSkill(skill)}
                          disabled={isAdded}
                          className={cn(
                            "flex items-center justify-between",
                            isAdded && "opacity-50"
                          )}
                        >
                          <span>{skill.title}</span>
                          {isAdded ? (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1.5 py-0 h-4"
                            >
                              Added
                            </Badge>
                          ) : (
                            <ChevronRight className="size-3.5 text-muted-foreground/40" />
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))}
              </CommandList>
            </Command>
          ) : (
            <div className="border-t px-5 py-5 space-y-4">
              <div className="flex items-center gap-3 rounded-lg bg-muted/30 px-3 py-2.5">
                <Wrench className="size-4 text-emerald-600 dark:text-emerald-400" />
                <span className="text-sm font-medium">{selectedSkill.title}</span>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">
                  Years of Experience (optional)
                </label>
                <Select
                  value={selectedExperienceLevel}
                  onValueChange={setSelectedExperienceLevel}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select experience" />
                  </SelectTrigger>
                  <SelectContent>
                    {experienceLevels.map((level) => (
                      <SelectItem key={level.id} value={level.id}>
                        {level.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedSkill(null)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAdd}
                  className="flex-1"
                >
                  Add Skill
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
});
